import React, { useEffect, useRef, useState, Fragment } from 'react';
import styled from 'styled-components';
import { find, findIndex, indexOf } from 'ramda';
import { COLOR } from '~~elements/Const';
import toast from '~~elements/Toast';
import Button from '~~elements/Button';
import Input from '~~elements/Input';
import { removeHideChar } from '~~utils/CommonUtils';
import { PlayerResource, GameResource } from '~~apis/resource';
import { GameSectionDiv } from '../styled';
import { boxBattingColumns, boxPitchingColumns, boxFieldingColumns } from '../Const';
import { useTournamentState } from '../useTournament';
import moment from 'moment';

const StyledDiv = styled.div`
  width: 100%;
  .admin-only {
    &--title {
      font-size: 1.1rem;
      margin: 0;
      padding: 0.5rem 0 0.2rem;
      border-bottom: 1px solid ${COLOR.secondary};
      display: inline-block;
    }
    &--content {
      width: 100%;
      display: block;
      overflow-x: auto;
    }
  }

  .notification {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    > p {
      margin: 0.2rem 0;
    }
    .success {
      color: green;
    }
    .warning {
      color: gold;
    }
    .action-required {
      color: red;
    }
  }
`;

const columns = [];
for (let k = 'A'.charCodeAt(0); k <= 'Z'.charCodeAt(0); k++) {
  columns.push(String.fromCharCode(k));
}
for (let j = 'A'.charCodeAt(0); j <= 'C'.charCodeAt(0); j++) {
  for (let k = 'A'.charCodeAt(0); k <= 'Z'.charCodeAt(0); k++) {
    columns.push(String.fromCharCode(j) + String.fromCharCode(k));
  }
}

const SHEETS_SCOPE = 'https://www.googleapis.com/auth/spreadsheets.readonly';

const getColumnIndex = (columnName) => {
  return indexOf(columnName, columns);
};

// 只有 Rebas 管理員 + 雙和聯盟才能使用
const GameG66NSection = ({ awayTeamPlayers, homeTeamPlayers, gameWithPlayers }) => {
  const { organizerUniqid, tournamentInfo } = useTournamentState();

  const [googleAuthDisplay, setGoogleAuthDisplay] = useState(false);
  const [googleAuthPassed, setGoogleAuthPassed] = useState(false);

  const [sheetLink, setSheetLink] = useState('');
  const [apiInProgress, setApiInProgress] = useState(false);

  const [awayPlayerNotifications, setAwayPlayerNotifications] = useState(null);
  const [homePlayerNotifications, setHomePlayerNotifications] = useState(null);
  const [pendingData, setPendingData] = useState(null);
  const [parsingErrors, setParsingErrors] = useState([]);

  const tokenClientRef = useRef(null);

  // gapi.auth2 已被 Google 改走 FedCM，只能登入、拿不到 Sheets scope
  // 改成 gapi.client 只負責呼叫 API，授權交給 Google Identity Services (GIS) 的 token client
  useEffect(() => {
    let gapiReady = false;
    let gisReady = false;
    const showAuthIfReady = () => {
      if (gapiReady && gisReady) {
        setGoogleAuthDisplay(true);
      }
    };

    const initGapiClient = () => {
      window.gapi.client
        .init({
          apiKey: process.env.REACT_APP_GOOGLE_ORG_COMPATIBLE_TOOLS_API_KEY,
          discoveryDocs: ['https://sheets.googleapis.com/$discovery/rest?version=v4'],
        })
        .then(() => {
          gapiReady = true;
          showAuthIfReady();
        })
        .catch((error) => {
          console.warn(error);
        });
    };

    const initTokenClient = () => {
      tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        scope: SHEETS_SCOPE,
        callback: (tokenResponse) => {
          if (tokenResponse.error) {
            console.warn(tokenResponse);
            toast(`Google 授權失敗：${tokenResponse.error}`, { status: 'error', second: 5 });
            return;
          }
          if (!window.google.accounts.oauth2.hasGrantedAllScopes(tokenResponse, SHEETS_SCOPE)) {
            toast('沒有拿到讀取 Google Sheet 的權限，請重新授權並勾選試算表權限', { status: 'error', second: 5 });
            return;
          }

          window.gapi.client.setToken({ access_token: tokenResponse.access_token });
          setGoogleAuthDisplay(false);
          setGoogleAuthPassed(true);
        },
      });
      gisReady = true;
      showAuthIfReady();
    };

    const loadScript = (src, onload) => {
      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.src = src;
      script.onload = onload;
      document.body.appendChild(script);
    };

    loadScript('https://apis.google.com/js/api.js', () => window.gapi.load('client', initGapiClient));
    loadScript('https://accounts.google.com/gsi/client', initTokenClient);
  }, []);

  const getAuth = () => {
    tokenClientRef.current.requestAccessToken();
  };

  const parseOneSide = (oneSideSheet) => {
    const batterStartRow = 8;
    const batterEndRow = 34;
    const batterPositionColumn = 'F';
    const batterNameColumn = 'G';
    const batterNumberColumn = 'H';
    const batterColumns = {
      PA: 'BF',
      AB: 'BG',
      RBI: 'BH',
      R: 'BI',
      SB: 'BU',
      CS: 'BV',
    };

    const pitcherStartRow = 45;
    const pitcherEndRow = 52;
    const pitcherNameColumn = 'G';
    const pitcherNumberColumn = 'H';
    const pitcherRecordColumn = 'I';
    const pitcherColumns = {
      IP: 'K',
      ballCount: 'N',
      BF: 'Q',
      H: 'W',
      HR: 'Z',
      BB: 'AB',
      HB: 'AD',
      SO: 'AF',
      WP: 'AH',
      BK: 'AJ',
      R: 'AL',
      ER: 'AN',
    };

    const catcherStartRow = 45;
    const catcherEndRow = 52;
    const catcherNameColumn = 'AP';
    const catcherNumberColumn = 'AV';
    const catcherColumns = {
      PB: 'AW',
      SB: 'AZ',
      CS: 'BC',
    };

    const orderPAResultRows = [10, 13, 16, 19, 22, 25, 28, 31, 34];
    const orderPAResultColumns = ['L', 'P', 'T', 'X', 'AB', 'AF', 'AJ', 'AN', 'AR', 'AV', 'AZ', 'BD'];

    const PABatterOrder = new Array(9).fill('').map(() => []);
    const batters = [];
    const pitchers = [];
    const players = [];
    const records = [];

    const addInGamePlayer = (name, number, position, isFirst) => {
      const newFielding = {};
      boxFieldingColumns.forEach((column) => {
        newFielding[column] = 0;
      });
      delete newFielding.player;
      newFielding.position = position;
      newFielding.order = 1;

      const playerIndex = findIndex((p) => p.info.name === name && p.info.number === number, players);
      if (playerIndex >= 0) {
        const positionIndex = findIndex((f) => f.position === position, players[playerIndex].fieldings);
        if (positionIndex < 0) {
          if (isFirst) {
            players[playerIndex].fieldings.unshift(newFielding);
          } else {
            players[playerIndex].fieldings.push(newFielding);
          }

          players[playerIndex].fieldings.forEach((fielding, i) => {
            fielding.order = i + 1;
          });
        }
      } else {
        players.push({
          info: {
            name,
            number,
          },
          fieldings: [newFielding],
        });
      }
    };

    const getIPOutFromIPString = (IP) => {
      if (IP.match(/ /)) {
        const [inning, out] = IP.split(' ');
        let outs = parseInt(inning) * 3;
        if (out) {
          outs += parseInt(out.split('/')[0]);
        }
        return outs;
      }

      if (IP.match(/\//)) {
        return parseInt(IP.split('/')[0]);
      }

      return parseInt(IP) * 3;
    };

    console.log(oneSideSheet);
    for (let yIndex = batterStartRow - 1; yIndex <= batterEndRow - 1; yIndex++) {
      const row = oneSideSheet[yIndex];
      const batterName = row[getColumnIndex(batterNameColumn)];
      if (!batterName) {
        continue;
      }

      const batterNumber = row[getColumnIndex(batterNumberColumn)];
      const batterOrder = Math.floor((yIndex - batterStartRow + 1) / 3) + 1;
      const batterPA = parseInt(row[getColumnIndex(batterColumns.PA)]);
      if (!Number.isNaN(batterPA)) {
        for (let PACount = 1; PACount <= batterPA; PACount++) {
          PABatterOrder[batterOrder - 1].push({ name: batterName, number: batterNumber });
        }
      }

      const batterDuplicate = findIndex(
        (b) => b.player.name === batterName && b.player.number === batterNumber,
        batters,
      );

      if (batterDuplicate >= 0) {
        Object.keys(batterColumns).forEach((columnName) => {
          const value = parseInt(row[getColumnIndex(batterColumns[columnName])]);
          if (Number.isNaN(value)) {
            return;
          }

          batters[batterDuplicate][columnName] += value;
        });
        continue;
      }

      const newBatter = {};
      boxBattingColumns.forEach((column) => {
        newBatter[column] = 0;
      });
      newBatter.player = { name: batterName, number: batterNumber, uniqid: '' };
      newBatter.is_PH = !!find((b) => b.order === batterOrder, batters);
      newBatter.order = batterOrder;
      Object.keys(batterColumns).forEach((columnName) => {
        const value = parseInt(row[getColumnIndex(batterColumns[columnName])]);
        if (Number.isNaN(value)) {
          return;
        }

        newBatter[columnName] = value;
      });
      batters.push(newBatter);

      const position = row[getColumnIndex(batterPositionColumn)];
      if (position) {
        addInGamePlayer(batterName, batterNumber, position, false);
      }
    }

    const resultMapping = {
      '1B': 'H',
      '2B': 'TWOB',
      '3B': 'THREEB',
      HR: 'HR',
      K: 'SO',
      KW: 'SO',
      B: 'BB',
      IB: 'IBB',
      D: 'HBP',
      SF: 'SF',
      SH: 'SH',
      FC: 'FC',
      E: 'E',
    };
    orderPAResultRows.forEach((orderPAResultRow, rowIndex) => {
      const row = oneSideSheet[orderPAResultRow - 1];
      let orderPAResultSeq = 0;
      for (let orderPAResultColumn of orderPAResultColumns) {
        const PAResult = row[getColumnIndex(orderPAResultColumn)];
        if (!PAResult) {
          continue;
        }

        const batterInfo = PABatterOrder[rowIndex][orderPAResultSeq];
        if (!batterInfo) {
          setParsingErrors((pre) => [...pre, `第${rowIndex + 1}棒打席數與實際打席結果數量不符！請上傳後手動修正`]);
          continue;
        }

        const batterIndex = findIndex(
          (b) => b.player.name === batterInfo.name && b.player.number === batterInfo.number,
          batters,
        );
        if (PAResult.match(/\./)) {
          const shortResult = PAResult.split('.')[1];
          if (!resultMapping[shortResult]) {
            setParsingErrors((pre) => [...pre, `無法辨識打席結果 ${PAResult}，略過`]);
          } else {
            batters[batterIndex][resultMapping[shortResult]]++;
            if (['2B', '3B', 'HR'].includes(shortResult)) {
              batters[batterIndex].H++;
            }
            if (['IB'].includes(shortResult)) {
              batters[batterIndex].BB++;
            }
          }
        } else if (PAResult.match(/KW/)) {
          batters[batterIndex].SO++;
        } else if (PAResult.match(/SF/)) {
          batters[batterIndex].SF++;
        } else if (PAResult.match(/SH/)) {
          batters[batterIndex].SH++;
        } else if (PAResult.match(/DP/)) {
          if (PAResult.match(/︶/)) {
            batters[batterIndex].GIDP++;
          } else {
            batters[batterIndex].FO++;
          }
        } else if (PAResult.match(/︶/)) {
          batters[batterIndex].GO++;
        } else if (PAResult.match(/︵/) || PAResult.match(/—/)) {
          batters[batterIndex].FO++;
        } else {
          setParsingErrors((pre) => [...pre, `無法辨識打席結果 ${PAResult}，略過`]);
        }
        orderPAResultSeq++;
      }
    });

    for (let yIndex = pitcherStartRow - 1; yIndex <= pitcherEndRow - 1; yIndex++) {
      const row = oneSideSheet[yIndex];
      const pitcherName = row[getColumnIndex(pitcherNameColumn)];
      if (!pitcherName) {
        continue;
      }

      const pitcherNumber = row[getColumnIndex(pitcherNumberColumn)];
      const pitcherDuplicate = findIndex(
        (b) => b.player.name === pitcherName && b.player.number === pitcherNumber,
        pitchers,
      );

      if (pitcherDuplicate >= 0) {
        Object.keys(pitcherColumns).forEach((columnName) => {
          if (columnName === 'IP') {
            pitchers[pitcherDuplicate].IPOut += getIPOutFromIPString(row[getColumnIndex(pitcherColumns[columnName])]);
            return;
          }

          const value = parseInt(row[getColumnIndex(pitcherColumns[columnName])]);
          if (Number.isNaN(value)) {
            return;
          }

          pitchers[pitcherDuplicate][columnName] += value;
        });
        continue;
      }

      const newPitcher = {};
      boxPitchingColumns.forEach((column) => {
        newPitcher[column] = 0;
      });
      newPitcher.player = { name: pitcherName, number: pitcherNumber, uniqid: '' };
      newPitcher.order = pitchers.length + 1;
      Object.keys(pitcherColumns).forEach((columnName) => {
        if (columnName === 'IP') {
          newPitcher.IPOut = getIPOutFromIPString(row[getColumnIndex(pitcherColumns[columnName])]);
          return;
        }

        const value = parseInt(row[getColumnIndex(pitcherColumns[columnName])]);
        if (Number.isNaN(value)) {
          return;
        }

        newPitcher[columnName] = value;
      });
      pitchers.push(newPitcher);

      addInGamePlayer(pitcherName, pitcherNumber, 'P', yIndex === pitcherStartRow - 1);

      const pitchingRecord = row[getColumnIndex(pitcherRecordColumn)];
      if (['W', 'L', 'SV'].includes(pitchingRecord)) {
        records.push({
          player: { name: pitcherName, number: pitcherNumber, uniqid: '' },
          player_type: 'PITCHER',
          value: pitchingRecord,
          comment: '',
        });
      }
    }

    for (let yIndex = catcherStartRow - 1; yIndex <= catcherEndRow - 1; yIndex++) {
      const row = oneSideSheet[yIndex];
      const catcherName = row[getColumnIndex(catcherNameColumn)];
      if (!catcherName) {
        continue;
      }

      const catcherNumber = row[getColumnIndex(catcherNumberColumn)];
      addInGamePlayer(catcherName, catcherNumber, 'C', false);

      const playerIndex = findIndex((p) => p.info.name === catcherName && p.info.number === catcherNumber, players);
      const positionIndex = findIndex((f) => f.position === 'C', players[playerIndex].fieldings);
      Object.keys(catcherColumns).forEach((columnName) => {
        const value = parseInt(row[getColumnIndex(catcherColumns[columnName])]);
        if (Number.isNaN(value)) {
          return;
        }

        players[playerIndex].fieldings[positionIndex][columnName] = value;
      });
    }

    console.log(batters);
    console.log(pitchers);
    console.log(players);
    console.log(records);
    return {
      batters,
      pitchers,
      players,
      records,
    };
  };

  const parseMedia = (media, mediaHyperlinks) => {
    console.log(media);
    console.log(mediaHyperlinks);
    const relatedLinks = [];
    (!media ? [] : media).forEach((linkInfo, index) => {
      if (linkInfo.length < 7) {
        return;
      }

      const link = mediaHyperlinks[index];
      if (!link) {
        return;
      }

      const title = `${linkInfo[0]}/${linkInfo[2]}(${linkInfo[3]})/${removeHideChar(linkInfo[4])}(${linkInfo[6]})`;
      relatedLinks.push({
        title,
        link,
      });
    });

    console.log(relatedLinks);
    return relatedLinks;
  };

  const parseScoreBoard = (home) => {
    const startInningColumnIndex = getColumnIndex('P');
    const inningWidth = 2;
    const maxInning = 12;

    const awayScoreRow = 3;
    const homeScoreRow = 4;

    const awayScores = [];
    const homeScores = [];
    for (
      let columnIndex = startInningColumnIndex, inning = 1;
      inning <= maxInning;
      columnIndex += inningWidth, inning++
    ) {
      const awayScoreString = home[awayScoreRow - 1][columnIndex];
      const homeScoreString = home[homeScoreRow - 1][columnIndex];
      if (awayScoreString) {
        const awayScore = parseInt(awayScoreString);
        if (!Number.isNaN(awayScore)) {
          awayScores.push(awayScoreString);
        }
      }
      if (homeScoreString) {
        const homeScore = parseInt(homeScoreString);
        if (!Number.isNaN(homeScore)) {
          homeScores.push(homeScoreString);
        }
      }
    }

    if (homeScores.length < awayScores.length) {
      homeScores.push('-');
    }

    const awayRuns = home[awayScoreRow - 1][getColumnIndex('AN')];
    const homeRuns = home[homeScoreRow - 1][getColumnIndex('AN')];

    console.log(awayRuns);
    console.log(homeRuns);
    console.log(awayScores);
    console.log(homeScores);
    return {
      awayRuns: parseInt(awayRuns),
      homeRuns: parseInt(homeRuns),
      awayScores,
      homeScores,
    };
  };

  const playerMapping = (batters, pitchers, players, records, currentTeamPlayers) => {
    const nameMatchNumberNotList = [];
    const noMatchWaitingConfirmList = [];

    const check = (playerList, playerKey) => {
      playerList.forEach((p) => {
        const playerInfo = p[playerKey];
        const fullMatched = find(
          (tp) => tp.name === playerInfo.name && tp.number === playerInfo.number,
          currentTeamPlayers,
        );
        if (fullMatched) {
          return;
        }

        const nameMatched = find((tp) => tp.name === playerInfo.name, currentTeamPlayers);
        if (nameMatched) {
          const nameMatchNumberNot = find(
            (i) => i.name === playerInfo.name && i.number === playerInfo.number,
            nameMatchNumberNotList,
          );
          if (!nameMatchNumberNot) {
            nameMatchNumberNotList.push({
              name: playerInfo.name,
              number: playerInfo.number,
              matchedNumber: nameMatched.number,
            });
          }

          p[playerKey].number = nameMatched.number;
          return;
        }

        const noMatchWaitingConfirm = find(
          (i) => i.name === playerInfo.name && i.number === playerInfo.number,
          noMatchWaitingConfirmList,
        );
        if (!noMatchWaitingConfirm) {
          noMatchWaitingConfirmList.push({ name: playerInfo.name, number: playerInfo.number });
        }
      });
    };
    check(batters, 'player');
    check(pitchers, 'player');
    check(players, 'info');
    check(records, 'player');

    console.log(nameMatchNumberNotList);
    console.log(noMatchWaitingConfirmList);
    return {
      nameMatchNumberNotList,
      noMatchWaitingConfirmList,
    };
  };

  const parseSheet = (away, home, media, mediaHyperlinks) => {
    const { batters: awayBatters, pitchers: awayPitchers, players: awayPlayers, records: awayRecords } = parseOneSide(
      away.values,
    );
    setAwayPlayerNotifications(playerMapping(awayBatters, awayPitchers, awayPlayers, awayRecords, awayTeamPlayers));

    const { batters: homeBatters, pitchers: homePitchers, players: homePlayers, records: homeRecords } = parseOneSide(
      home.values,
    );
    setHomePlayerNotifications(playerMapping(homeBatters, homePitchers, homePlayers, homeRecords, homeTeamPlayers));

    const relatedLinks = parseMedia(media.values, mediaHyperlinks);
    relatedLinks.push({ title: '逐打席紀錄', link: sheetLink });
    const { awayRuns, homeRuns, awayScores, homeScores } = parseScoreBoard(home.values);

    setPendingData({
      scoreboard: {
        awayRuns,
        homeRuns,
        awayScores,
        homeScores,
      },
      relatedLinks,
      stats: {
        away: {
          batters: awayBatters,
          pitchers: awayPitchers,
          players: awayPlayers,
          records: awayRecords,
        },
        home: {
          batters: homeBatters,
          pitchers: homePitchers,
          players: homePlayers,
          records: homeRecords,
        },
      },
    });
  };

  const getSheetContent = () => {
    const linkStrings = sheetLink.split('/');
    let sheetID = '';

    let isNext = false;
    for (let linkString of linkStrings) {
      if (isNext) {
        sheetID = linkString;
        break;
      }
      if (linkString !== 'd') {
        continue;
      }

      isNext = true;
    }

    if (!sheetID) {
      toast('無法從連結獲得試算表 ID', { status: 'error' });
      return;
    }

    let away = null;
    let home = null;
    let media = null;
    let mediaHyperlinks = null;

    let apiCountDown = 4;
    const apiDone = () => {
      apiCountDown--;
      if (apiCountDown !== 0) {
        return;
      }

      try {
        parseSheet(away, home, media, mediaHyperlinks);
      } catch (e) {
        console.error(e);
      }
      setApiInProgress(false);
    };

    setApiInProgress(true);
    window.gapi.client.sheets.spreadsheets.values
      .get({
        spreadsheetId: sheetID,
        range: '先攻!A1:BW53',
      })
      .then((response) => {
        const range = response.result;
        console.log('先攻', range);
        away = range;
        apiDone();
      })
      .catch((error) => {
        console.error(error);
        toast(`讀取 先攻 分頁失敗，請確定有這個分頁`, { status: 'error', second: 5 });
        setApiInProgress(false);
        return;
      });

    window.gapi.client.sheets.spreadsheets.values
      .get({
        spreadsheetId: sheetID,
        range: '後攻!A1:BW53',
      })
      .then((response) => {
        const range = response.result;
        console.log('後攻', range);
        home = range;
        apiDone();
      })
      .catch((error) => {
        console.error(error);
        toast(`讀取 後攻 分頁失敗，請確定有這個分頁`, { status: 'error', second: 5 });
        setApiInProgress(false);
        return;
      });

    window.gapi.client.sheets.spreadsheets.values
      .get({
        spreadsheetId: sheetID,
        range: '影音!C4:I8',
      })
      .then((response) => {
        const range = response.result;
        console.log('影音', range);
        media = range;
        apiDone();
      })
      .catch((error) => {
        console.error(error);
        toast(`讀取 影音 分頁失敗，請確定有這個分頁`, { status: 'error', second: 5 });
        setApiInProgress(false);
        return;
      });

    window.gapi.client.sheets.spreadsheets
      .get({
        spreadsheetId: sheetID,
        ranges: ['影音!C4:I8'],
        fields: 'sheets/data/rowData/values/hyperlink',
      })
      .then((response) => {
        console.log('影音（連結）', response.result);
        mediaHyperlinks = response.result.sheets[0].data[0].rowData.map((eachRow) => eachRow.values[5]?.hyperlink);
        apiDone();
      })
      .catch((error) => {
        console.error(error);
        toast(`讀取 影音（連結） 分頁失敗，請確定有這個分頁`, { status: 'error', second: 5 });

        // 影音可以放棄，以資料為主
        mediaHyperlinks = [];
        apiDone();

        // setApiInProgress(false);
        // return;
      });

    setAwayPlayerNotifications(null);
    setHomePlayerNotifications(null);
    setPendingData(null);
    setParsingErrors([]);
  };

  const removeNoMatchWaitingConfirm = (side, index) => () => {
    const noMatchWaitingConfirm =
      side === 'away'
        ? awayPlayerNotifications.noMatchWaitingConfirmList[index]
        : homePlayerNotifications.noMatchWaitingConfirmList[index];

    const sideStats = pendingData.stats[side];

    const removeStats = (playerList, playerKey) => {
      const playerIndex = findIndex(
        (p) => p[playerKey].name === noMatchWaitingConfirm.name && p[playerKey].number === noMatchWaitingConfirm.number,
        playerList,
      );
      if (playerIndex < 0) {
        return;
      }

      playerList.splice(playerIndex, 1);
    };
    removeStats(sideStats.batters, 'player');
    removeStats(sideStats.pitchers, 'player');
    removeStats(sideStats.players, 'info');
    removeStats(sideStats.records, 'player');

    if (side === 'away') {
      awayPlayerNotifications.noMatchWaitingConfirmList.splice(index, 1);
      setAwayPlayerNotifications({
        ...awayPlayerNotifications,
        noMatchWaitingConfirmList: [...awayPlayerNotifications.noMatchWaitingConfirmList],
      });
    } else {
      homePlayerNotifications.noMatchWaitingConfirmList.splice(index, 1);
      setHomePlayerNotifications({
        ...homePlayerNotifications,
        noMatchWaitingConfirmList: [...homePlayerNotifications.noMatchWaitingConfirmList],
      });
    }
  };

  const handleSubmit = () => {
    const awayNewPlayerAmount = awayPlayerNotifications.noMatchWaitingConfirmList.length;
    const homeNewPlayerAmount = homePlayerNotifications.noMatchWaitingConfirmList.length;

    setApiInProgress(true);

    const scheduledStartAt = new Date(gameWithPlayers.info.time.scheduled_start_at);
    const endedAt = moment(scheduledStartAt).add(2, 'hours').toDate();
    const scoreboardData = {
      ...gameWithPlayers,
      info: {
        ...gameWithPlayers.info,
        innings: pendingData.scoreboard.awayScores.length,
        runs_without_innings: false,
        status: 'FINISHED',
        time: {
          scheduled_start_at: moment(scheduledStartAt).format('YYYY/MM/DD HH:mm'),
          started_at: moment(scheduledStartAt).format('YYYY/MM/DD HH:mm'),
          ended_at: moment(endedAt).format('YYYY/MM/DD HH:mm'),
        },
      },
      away: {
        ...gameWithPlayers.away,
        scores: pendingData.scoreboard.awayScores,
        runs: pendingData.scoreboard.awayRuns,
      },
      home: {
        ...gameWithPlayers.home,
        scores: pendingData.scoreboard.homeScores,
        runs: pendingData.scoreboard.homeRuns,
      },
    };

    let createPlayerCountDown = awayNewPlayerAmount + homeNewPlayerAmount;
    const apiDone = () => {
      createPlayerCountDown--;
      if (createPlayerCountDown > 0) {
        return;
      }

      GameResource.updateScoreBoard({
        seasonUniqid: tournamentInfo.uniqid,
        gameUniqid: gameWithPlayers.uniqid,
        data: scoreboardData,
      })
        .then(() => {
          if (pendingData.relatedLinks.length > 0) {
            GameResource.updateRelatedLinks({
              seasonUniqid: tournamentInfo.uniqid,
              gameUniqid: gameWithPlayers.uniqid,
              data: pendingData.relatedLinks,
            }).catch((e) => {
              console.error(e);
              toast(`更新相關連結發生錯誤，請重新整理此頁面後再試一次。${e.toString()}`, {
                status: 'error',
                second: 5,
              });
            });
          }

          GameResource.updateStats({
            seasonUniqid: tournamentInfo.uniqid,
            gameUniqid: gameWithPlayers.uniqid,
            data: {
              side: 'AWAY',
              ...pendingData.stats.away,
            },
          })
            .then(() => {
              GameResource.updateStats({
                seasonUniqid: tournamentInfo.uniqid,
                gameUniqid: gameWithPlayers.uniqid,
                data: {
                  side: 'HOME',
                  ...pendingData.stats.home,
                },
              })
                .then(() => {
                  toast('更新成功！請至前台確認', { status: 'success', second: 2 });
                  setSheetLink('');
                  setApiInProgress(false);
                  setPendingData(null);
                  setTimeout(() => {
                    window.location.reload();
                  }, 1000);
                })
                .catch((e) => {
                  console.error(e);
                  toast(`更新主場數據發生錯誤，請重新整理此頁面後再試一次。${e.toString()}`, {
                    status: 'error',
                    second: 5,
                  });
                });
            })
            .catch((e) => {
              console.error(e);
              toast(`更新客場數據發生錯誤，請重新整理此頁面後再試一次。${e.toString()}`, {
                status: 'error',
                second: 5,
              });
            });
        })
        .catch((e) => {
          console.error(e);
          toast(`發生錯誤，請重新整理此頁面後再試一次。${e.toString()}`, { status: 'error', second: 5 });
        });
    };

    if (createPlayerCountDown === 0) {
      apiDone();
      return;
    }

    const getNoNumberSeq = (players, preSeq) => {
      let noNumberSeq = 1;
      if (preSeq) {
        noNumberSeq = preSeq + 1;
      }

      while (!!find((p) => p.number.match(new RegExp(`$${noNumberSeq}`)), players)) {
        noNumberSeq++;
      }

      return noNumberSeq;
    };

    const getNonDuplicateNumber = (players, usedNumbers, number) => {
      const numbers = [...players.map((p) => p.number), usedNumbers];
      while (numbers.includes(number)) {
        number += '*';
      }

      return number;
    };

    const changeNumber = (playerList, playerKey, originName, originNumber, newNumber) => {
      const playerIndex = findIndex(
        (p) => p[playerKey].name === originName && p[playerKey].number === originNumber,
        playerList,
      );
      if (playerIndex < 0) {
        return;
      }

      playerList[playerIndex][playerKey].number = newNumber;
    };

    const oneSideCreatePlayer = (noMatchWaitingConfirmList, teamPlayers, side) => {
      let noNumberSeq = 0;
      const usedNumbers = [];
      noMatchWaitingConfirmList.forEach((noMatchWaitingConfirm) => {
        const originNumber = noMatchWaitingConfirm.number;
        if (!noMatchWaitingConfirm.number) {
          noNumberSeq = getNoNumberSeq(teamPlayers, noNumberSeq);
          noMatchWaitingConfirm.number = `$${noNumberSeq}`;
        } else {
          const newNumber = getNonDuplicateNumber(teamPlayers, usedNumbers, noMatchWaitingConfirm.number);
          usedNumbers.push(newNumber);
          noMatchWaitingConfirm.number = newNumber;
        }
        changeNumber(
          pendingData.stats[side].batters,
          'player',
          noMatchWaitingConfirm.name,
          originNumber,
          noMatchWaitingConfirm.number,
        );
        changeNumber(
          pendingData.stats[side].pitchers,
          'player',
          noMatchWaitingConfirm.name,
          originNumber,
          noMatchWaitingConfirm.number,
        );
        changeNumber(
          pendingData.stats[side].players,
          'info',
          noMatchWaitingConfirm.name,
          originNumber,
          noMatchWaitingConfirm.number,
        );
        changeNumber(
          pendingData.stats[side].records,
          'player',
          noMatchWaitingConfirm.name,
          originNumber,
          noMatchWaitingConfirm.number,
        );

        PlayerResource.createPlayerInSeasonTeamByOrganizer({
          leagueUniqid: organizerUniqid,
          seasonUniqid: tournamentInfo.uniqid,
          seasonTeamUniqid: gameWithPlayers[side].season_team_uniqid,
          data: {
            player_uniqid: '',
            number: noMatchWaitingConfirm.number,
            name: noMatchWaitingConfirm.name,
            avatar_path: '',
            birthday: '',
            first_position: '',
            second_position: '',
            p_hand: '',
            b_hand: '',
          },
        })
          .then(() => {
            apiDone();
          })
          .catch((e) => {
            console.error(e);
            toast(`發生錯誤，請重新整理此頁面後再試一次。${e.toString()}`, { status: 'error', second: 5 });
          });
      });
    };

    oneSideCreatePlayer(awayPlayerNotifications.noMatchWaitingConfirmList, awayTeamPlayers, 'away');
    oneSideCreatePlayer(homePlayerNotifications.noMatchWaitingConfirmList, homeTeamPlayers, 'home');
  };

  return (
    <GameSectionDiv>
      <h3 className="section--title">Rebas 管理員專用</h3>
      <div className="section--description">請注意！主辦方管理者無法使用這介面</div>
      <StyledDiv>
        {googleAuthDisplay && (
          <p>
            請先授權讀取 Google Sheet{' '}
            <Button color="red" onClick={getAuth}>
              授權 Google 讀取資料
            </Button>
          </p>
        )}
        {googleAuthPassed && (
          <Fragment>
            <h4 className="admin-only--title">讀取雙和 Google Sheet 連結後自動分析</h4>
            <div className="admin-only--content">
              <label>貼上連結</label>
              <Input
                value={sheetLink}
                onChange={(e) => setSheetLink(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/1r0HJuIoC-b1Gowc6CuFi9WIQ1Xx6bTjzLSk7Iw-Hbzw/edit#gid=1521201990"
              />
              <Button width="5rem" color="blue" onClick={getSheetContent} disabled={!sheetLink.trim() || apiInProgress}>
                分析
              </Button>
            </div>
            {pendingData && (
              <Fragment>
                <h4 className="admin-only--title">分析結果</h4>
                <div className="admin-only--content">
                  <p>
                    綠字為自動比對成功；黃字為無法解析系統忽略（如有需求請更新後去數據那邊修改）；紅字為需要手動確認（如取消將直接刪除該紀錄）
                  </p>
                  <div className="notification">
                    <p>客場</p>
                    {awayPlayerNotifications.nameMatchNumberNotList.map((nameMatchNumberNot, i) => (
                      <p key={i} className="success">
                        {`選手${nameMatchNumberNot.name} `}
                        {`背號${nameMatchNumberNot.number ? nameMatchNumberNot.number : '無'} `}
                        {`-> 匹配到同名選手，背號${nameMatchNumberNot.matchedNumber}`}
                      </p>
                    ))}
                    {awayPlayerNotifications.noMatchWaitingConfirmList.map((noMatchWaitingConfirm, i) => (
                      <p key={i} className="action-required">
                        <Button width="unset" mini color="red" onClick={removeNoMatchWaitingConfirm('away', i)}>
                          X
                        </Button>
                        {`選手${noMatchWaitingConfirm.name} `}
                        {`背號${noMatchWaitingConfirm.number ? noMatchWaitingConfirm.number : '無'} `}
                        {`-> 找不到相符選手，X 將刪除該選手紀錄。保留則會新增選手`}
                      </p>
                    ))}
                    <p>主場</p>
                    {homePlayerNotifications.nameMatchNumberNotList.map((nameMatchNumberNot, i) => (
                      <p key={i} className="success">
                        {`選手${nameMatchNumberNot.name} `}
                        {`背號${nameMatchNumberNot.number ? nameMatchNumberNot.number : '無'} `}
                        {`-> 匹配到同名選手，背號${nameMatchNumberNot.matchedNumber}`}
                      </p>
                    ))}
                    {homePlayerNotifications.noMatchWaitingConfirmList.map((noMatchWaitingConfirm, i) => (
                      <p key={i} className="action-required">
                        <Button width="unset" mini color="red" onClick={removeNoMatchWaitingConfirm('home', i)}>
                          X
                        </Button>
                        {`選手${noMatchWaitingConfirm.name} `}
                        {`背號${noMatchWaitingConfirm.number ? noMatchWaitingConfirm.number : '無'} `}
                        {`-> 找不到相符選手，X 將刪除該選手紀錄。保留則會新增選手`}
                      </p>
                    ))}
                    <p>無分類</p>
                    {parsingErrors.map((err, i) => (
                      <p key={i} className="warning">
                        {err}
                      </p>
                    ))}
                  </div>
                </div>
                <Button width="unset" onClick={handleSubmit} disabled={apiInProgress}>
                  送出（更新計分板、相關連結、主客場 BoxScore）
                </Button>
              </Fragment>
            )}
          </Fragment>
        )}
      </StyledDiv>
    </GameSectionDiv>
  );
};

export default GameG66NSection;
